import type { Analytics } from "./analytics.js";
import { AUTO_EVENTS } from "mindbricks-analytics-shared";

/**
 * Wires browser lifecycle events to the analytics core.
 *
 *   - visibilitychange  → $tab_hidden / $tab_visible + active/inactive timing
 *   - pagehide          → $session_end + flushBeacon (most reliable on mobile)
 *   - beforeunload      → $session_end + flushBeacon (desktop fallback)
 *   - online / offline  → $network_online / $network_offline
 *
 * The unload-time $session_end is deduped via a `unloadFired` flag so the
 * pair-firing of pagehide + beforeunload only emits one event.
 */
export function attachLifecycle(a: Analytics): () => void {
  if (typeof window === "undefined") return () => {};

  let unloadFired = false;
  const fireSessionEnd = (): void => {
    if (unloadFired) return;
    unloadFired = true;
    a.trackInternal(AUTO_EVENTS.SESSION_END, {
      reason: "unload",
      queueSize: a.queueSize(),
    });
    a.flushBeacon();
  };

  const onVisibility = (): void => {
    const visible = !document.hidden;
    a.recordVisibility(visible);
    if (visible) {
      a.trackInternal(AUTO_EVENTS.TAB_VISIBLE, {});
    } else {
      a.trackInternal(AUTO_EVENTS.TAB_HIDDEN, {});
      // The most reliable place to flush on mobile is on visibility-hidden.
      a.flushBeacon();
    }
  };

  const onPageHide = (): void => fireSessionEnd();
  const onBeforeUnload = (): void => fireSessionEnd();

  const onOnline = (): void => {
    a.trackInternal(AUTO_EVENTS.NETWORK_ONLINE, {});
    void a.flush();
  };
  const onOffline = (): void => {
    a.trackInternal(AUTO_EVENTS.NETWORK_OFFLINE, {});
  };

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("beforeunload", onBeforeUnload);
  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);

  return () => {
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("beforeunload", onBeforeUnload);
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
  };
}
